import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import { useCallback } from 'react';
import { CvsFeeScheduleForm } from '../components/cvs-fee-schedules/cvs-fee-schedule-form/form';
import {
  CvsFeeScheduleFormValues,
  displayValueToCharge,
  displayValueToDaysSupply,
} from '../components/cvs-fee-schedules/cvs-fee-schedule-form/validation-schema';
import { CvsFeeScheduleGrid } from '../components/cvs-fee-schedules/cvs-fee-schedule-grid';
import { FeeScheduleViewToggle } from '../components/fee-schedules/fee-schedule-view-toggle';
import {
  DynamicDrawerProvider,
  useDynamicDrawerContext,
} from '../context/dynamic-drawer.context';
import { useSnackbarContext } from '../context/snackbar.context';
import { useBillingWriteAccess } from '../hooks/use-billing-write-access';
import {
  PaymentType,
  useCreateCvsFeeScheduleMutation,
  useGetPaymentTypesQuery,
} from '../types';

export const CVS_FEE_SCHEDULES_TEST_ID = 'cvs-fee-schedules-container';

const EMPTY_PAYMENT_TYPES: PaymentType[] = [];

const CvsFeeSchedulesContent = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const { data: paymentTypesData, loading: paymentTypesLoading } =
    useGetPaymentTypesQuery();
  const paymentTypes = paymentTypesData?.getPaymentTypes ?? EMPTY_PAYMENT_TYPES;

  const [createCvsFeeSchedule] = useCreateCvsFeeScheduleMutation();

  const handleCreate = useCallback(
    async (values: CvsFeeScheduleFormValues): Promise<void> => {
      try {
        await createCvsFeeSchedule({
          variables: {
            // Convert form values to CVS storage formats (zero-padded cents/days)
            cvsFeeScheduleInput: {
              paymentType: values.paymentType || null,
              price: values.price as number,
              upc: values.upc,
              charge: displayValueToCharge(values.charge),
              daysSupply: displayValueToDaysSupply(values.daysSupply),
              description: values.description || null,
            },
          },
          refetchQueries: ['getCvsFeeSchedules'],
        });
        setSnackbarState(
          'CVS fee schedule created successfully',
          'success',
          3000,
        );
        onClose();
      } catch {
        setSnackbarState('Error creating CVS fee schedule', 'error', 3000);
      }
    },
    [createCvsFeeSchedule, setSnackbarState, onClose],
  );

  const handleAddRecord = (): void => {
    setDrawerContent(
      <HHStack padding={4}>
        <CvsFeeScheduleForm
          onSubmit={handleCreate}
          paymentTypes={paymentTypes}
          paymentTypesLoading={paymentTypesLoading}
        />
      </HHStack>,
      <HHTypography hhVariant="h6">Add CVS UPC</HHTypography>,
      undefined,
      'temporary',
    );
  };

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={4}
      minHeight="0"
      data-testid={CVS_FEE_SCHEDULES_TEST_ID}
    >
      <HHStack
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHStack direction="row" alignItems="center" spacing={5}>
          <HHStack sx={{ minWidth: 320 }}>
            <HHTypography hhVariant="section-title">
              CVS UPC Schedules
            </HHTypography>
          </HHStack>
          <FeeScheduleViewToggle currentView="cvs" />
        </HHStack>
        {canWrite && (
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={handleAddRecord}
            data-testid="add-cvs-fee-schedule-button"
            sx={{ minWidth: 120 }}
          >
            Add UPC
          </HHButton>
        )}
      </HHStack>
      <CvsFeeScheduleGrid
        paymentTypes={paymentTypes}
        paymentTypesLoading={paymentTypesLoading}
      />
    </HHStack>
  );
};

const CvsFeeSchedules = (): JSX.Element => (
  <DynamicDrawerProvider>
    <CvsFeeSchedulesContent />
  </DynamicDrawerProvider>
);

export default CvsFeeSchedules;
